import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import classNames from 'classnames';
import { getSettings, loadSearchExperienceConfig } from 'actions';
import { WizardStepContainer } from './partials';
import LoadingDots from '../../../LoadingDots/LoadingDots';
import CircularProgress from '../../../placeholders/CircularProgress';
import IntlMessages from '../../../../../util/IntlMessages';
import { useRuntimeIndexing } from '../../../../../features/catalog-sync/useRuntimeIndexing';
import { isIndexingActive } from '../../../../../features/catalog-sync/indexingRuntimeUtils';

const UPDATE_FREQUENCY = 5 * 1000;

const iconFeedPending = require('assets/wizard/icon-catalog-feed.svg');
const iconFeedDone = require('assets/wizard/icon-catalog-feed-done.svg');

const iconImagesPending = require('assets/wizard/icon-catalog-images.svg');
const iconImagesDone = require('assets/wizard/icon-catalog-images-done.svg');

const iconProductsPending = require('assets/wizard/icon-catalog-products.svg');
const iconProductsDone = require('assets/wizard/icon-catalog-products-done.svg');

const iconCheckbox = require('assets/wizard/icon-catalog-done.svg');

export default function WizardCatalogIndexingStep({ onReady, useRuntimeStatus = false }) {
  const dispatch = useDispatch();

  const { search } = useSelector((state) => state.onboarding);
  const { catalogLoaded, catalogIndexed, imagesIndexed } = search;

  const { visualSearchEnabled, token } = useSelector((state) => state.settings);
  const runtimeIndexing = useRuntimeIndexing({
    historyLimit: 10,
    enabled: useRuntimeStatus,
  });
  const runtimeHasSuccessfulCatalog = Boolean(runtimeIndexing.snapshot.lastSuccessAt);
  const runtimeIsActive = isIndexingActive(runtimeIndexing.snapshot.current);
  const effectiveCatalogLoaded = useRuntimeStatus
    ? runtimeHasSuccessfulCatalog
    : catalogLoaded;
  const effectiveCatalogIndexed = useRuntimeStatus
    ? runtimeHasSuccessfulCatalog
    : catalogIndexed;

  useEffect(() => {
    if (useRuntimeStatus) return undefined;
    const interval = setInterval(() => {
      dispatch(getSettings(token));
      dispatch(loadSearchExperienceConfig(token));
    }, UPDATE_FREQUENCY);

    return () => clearInterval(interval);
  }, [dispatch, token, useRuntimeStatus]);

  const tasksBase = [
    {
      key: 'catalogLoaded',
      title: <IntlMessages id="onboarding.catalog.task.fetching" />,
      done: effectiveCatalogLoaded,
      iconPending: iconFeedPending,
      iconDone: iconFeedDone,
    },
    {
      key: 'catalogIndexed',
      title: <IntlMessages id="onboarding.catalog.task.indexing" />,
      done: effectiveCatalogIndexed,
      iconPending: iconProductsPending,
      iconDone: iconProductsDone,
    },
  ];

  if (visualSearchEnabled) {
    tasksBase.push({
      key: 'imagesIndexed',
      title: <IntlMessages id="onboarding.catalog.task.images" />,
      done: imagesIndexed,
      iconPending: iconImagesPending,
      iconDone: iconImagesDone,
    });
  }

  useEffect(() => {
    if (onReady) {
      const visualIsReady = !visualSearchEnabled || (visualSearchEnabled && imagesIndexed);
      onReady(effectiveCatalogLoaded && effectiveCatalogIndexed && visualIsReady);
    }
  }, [effectiveCatalogLoaded, effectiveCatalogIndexed, imagesIndexed, visualSearchEnabled, onReady]);

  const runtimeStatusText = (() => {
    if (!useRuntimeStatus) return null;
    if (runtimeIndexing.isLoading) return 'Checking the latest catalog update…';
    if (runtimeIndexing.error) return runtimeIndexing.error;
    if (runtimeIsActive) return 'Your catalog update is still running. This page will refresh automatically.';
    if (runtimeHasSuccessfulCatalog) return 'The latest catalog update completed successfully.';
    return 'No successful catalog update is available yet.';
  })();

  const tasks = tasksBase.map((t, idx) => {
    if (t.done) {
      return {
        ...t,
        status: 'done',
      };
    }
    const allPrevDone = tasksBase.slice(0, idx)
      .every((x) => x.done);
    if (allPrevDone) {
      return {
        ...t,
        status: 'active',
      };
    }
    return {
      ...t,
      status: 'pending',
    };
  });

  const TaskItem = ({ item }) => {
    let itemStateClass = '';
    let avatarClass = '';
    let iconClass = '';
    let textClass = '';
    const { status } = item;
    const isActive = (status === 'active');

    if (status === 'done') {
      itemStateClass = 'taskItem--done';
      avatarClass = 'avatarDone';
      iconClass = 'zmdi-check text-white';
      textClass = 'text-app-primary';
    } else if (isActive) {
      itemStateClass = 'taskItem--done';
      avatarClass = 'avatarActive';
      iconClass = 'zmdi-dot-circle animation-breath';
      textClass = 'text-app-primary';
    } else {
      itemStateClass = 'taskItem--pending';
      avatarClass = 'avatarPending';
      iconClass = 'text-muted';
      textClass = 'text-muted';
    }

    return (
      <div className={classNames('taskItem', itemStateClass)}>
        {isActive && <CircularProgress r={10} size={40} className="loader_small" />}
        {!isActive && status === 'done' && <img src={item.iconDone} alt="" />}
        {!isActive && status !== 'done' && <img src={item.iconPending} alt="" />}
        <Typography variant="subtitle1" className={classNames('task-item-text', textClass)}>
          {item.title}
          {isActive && <LoadingDots />}
        </Typography>
        {status === 'done' && <img src={iconCheckbox} alt="" />}
      </div>
    );
  };

  return (
    <div className="wizard-step wizard-catalog-step">
      <WizardStepContainer
        title={<IntlMessages id="onboarding.catalog.title" />}
      >
        <div className="row">
          <div className="col-10 offset-1">
            <p className="sub-header">
              <IntlMessages id="onboarding.catalog.subheader.line1" />
            </p>
            <p className="sub-header">
              <IntlMessages id="onboarding.catalog.subheader.line2" />
            </p>
          </div>
        </div>
        <div className="row mt-5">
          <div className="col-12">
            <div className="step-container">
              <div className="step-list">
                {tasks.map((item) => (
                  <TaskItem key={item.key} item={item} />
                ))}
              </div>
            </div>
          </div>
        </div>
        {runtimeStatusText && (
          <div className="row mt-3">
            <div className="col-12 text-center">
              <Typography
                variant="body2"
                color={runtimeIndexing.error ? 'error' : 'textSecondary'}
                data-testid="cloud-catalog-runtime-status"
              >
                {runtimeStatusText}
              </Typography>
            </div>
          </div>
        )}
      </WizardStepContainer>
    </div>
  );
}

WizardCatalogIndexingStep.propTypes = {
  onReady: PropTypes.func,
  useRuntimeStatus: PropTypes.bool,
};

WizardCatalogIndexingStep.defaultProps = {
  onReady: null,
  useRuntimeStatus: false,
};
